import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera } from "lucide-react";
import { useProfile, useUpdateProfile, useUploadProfilePicture } from "@/hooks/useApi";
import type { ProfilePatch } from "@/types";
import {
  SettingsField,
  SettingsInput,
  SettingsPageHeader,
  SettingsSection,
  SettingsTabs,
  SettingsTextarea,
  SettingsToggleRow,
} from "@/components/settings/settings-ui";

const TABS = [
  "General",
  "Multi-factor authentication",
  "Custom fields",
  "Email settings",
  "Conversations",
] as const;

type Tab = (typeof TABS)[number];

function splitName(full?: string): { first: string; last: string } {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

export function SettingsProfilePage() {
  const { data: profile, isLoading, error } = useProfile();
  const updateProfile = useUpdateProfile();
  const uploadPicture = useUploadProfilePicture();
  const pictureInputRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<Tab>("General");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("");
  const [form, setForm] = useState<ProfilePatch>({});
  const [initialized, setInitialized] = useState(false);
  const [linkedinMode, setLinkedinMode] = useState<"normal" | "salesnav">("salesnav");
  const [creditLimit, setCreditLimit] = useState("");
  const [mfaEmail, setMfaEmail] = useState(false);
  const [mfaApp, setMfaApp] = useState(false);
  const [pictureValidationError, setPictureValidationError] = useState<string>();

  useEffect(() => {
    if (!profile || initialized) return;
    const { first, last } = splitName(profile.name);
    setFirstName(first);
    setLastName(last);
    setTitle(profile.seniority ?? "");
    setForm({
      themePreference: profile.themePreference ?? "system",
      name: profile.name ?? "",
      email: profile.email ?? "",
      location: profile.location ?? "",
      targetSegments: profile.targetSegments ?? [],
      targetDealSizeMin: profile.targetDealSizeMin ?? 0,
      targetDealSizeMax: profile.targetDealSizeMax ?? 0,
      currency: profile.currency ?? "USD",
      seniority: profile.seniority ?? "",
      archetypes: profile.archetypes ?? "",
      narrative: profile.narrative ?? "",
      superpowers: profile.superpowers ?? [],
      proofPoints: profile.proofPoints ?? [],
      dealbreakers: profile.dealbreakers ?? [],
      assistantMemory: profile.assistantMemory ?? "",
    });
    setInitialized(true);
  }, [profile, initialized]);

  const hasChanges = useMemo(() => {
    if (!initialized || !profile) return false;
    const name = [firstName, lastName].filter(Boolean).join(" ");
    return (
      name !== (profile.name ?? "") ||
      title !== (profile.seniority ?? "") ||
      JSON.stringify(form) !==
        JSON.stringify({
          themePreference: profile.themePreference ?? "system",
          name: profile.name ?? "",
          email: profile.email ?? "",
          location: profile.location ?? "",
          targetSegments: profile.targetSegments ?? [],
          targetDealSizeMin: profile.targetDealSizeMin ?? 0,
          targetDealSizeMax: profile.targetDealSizeMax ?? 0,
          currency: profile.currency ?? "USD",
          seniority: profile.seniority ?? "",
          archetypes: profile.archetypes ?? "",
          narrative: profile.narrative ?? "",
          superpowers: profile.superpowers ?? [],
          proofPoints: profile.proofPoints ?? [],
          dealbreakers: profile.dealbreakers ?? [],
          assistantMemory: profile.assistantMemory ?? "",
        })
    );
  }, [initialized, profile, firstName, lastName, title, form]);

  async function handleSave() {
    const name = [firstName, lastName].filter(Boolean).join(" ");
    await updateProfile.mutateAsync({
      ...form,
      name,
      seniority: title || form.seniority,
    });
  }

  async function handlePictureChange(file?: File) {
    if (!file) return;
    setPictureValidationError(undefined);
    uploadPicture.reset();
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setPictureValidationError("Choose a JPEG, PNG, or WebP image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPictureValidationError("Choose an image smaller than 5 MB.");
      return;
    }
    try {
      await uploadPicture.mutateAsync(file);
    } catch {
      // The mutation error is displayed beside the picker.
    } finally {
      if (pictureInputRef.current) pictureInputRef.current.value = "";
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-destructive">Failed to load profile. Please try again later.</p>
    );
  }

  return (
    <div>
      <SettingsPageHeader
        title="Profile"
        onSave={() => void handleSave()}
        saving={updateProfile.isPending}
        saveDisabled={!hasChanges && tab === "General"}
      />
      <SettingsTabs tabs={[...TABS]} active={tab} onChange={(t) => setTab(t as Tab)} />

      {tab === "General" && (
        <>
          <SettingsSection title="Account Info">
            <div className="flex flex-wrap items-center gap-4 border-b border-border pb-5">
              {profile?.profilePictureUrl ? (
                <img
                  src={profile.profilePictureUrl}
                  alt="Your profile"
                  className="size-20 rounded-full border border-border object-cover"
                />
              ) : (
                <div className="flex size-20 items-center justify-center rounded-full bg-brand/15 text-xl font-semibold text-brand">
                  {(profile?.name ?? profile?.email ?? "?")
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((part) => part[0])
                    .join("")
                    .toUpperCase()}
                </div>
              )}
              <div>
                <input
                  ref={pictureInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="sr-only"
                  onChange={(event) => void handlePictureChange(event.target.files?.[0])}
                />
                <button
                  type="button"
                  disabled={uploadPicture.isPending}
                  onClick={() => pictureInputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-surface-hover disabled:opacity-50"
                >
                  <Camera className="size-4" />
                  {uploadPicture.isPending ? "Uploading…" : "Change picture"}
                </button>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  JPEG, PNG, or WebP. Maximum 5 MB.
                </p>
                {(pictureValidationError || uploadPicture.error) && (
                  <p className="mt-1 text-xs text-destructive">
                    {pictureValidationError ??
                    (uploadPicture.error instanceof Error
                      ? uploadPicture.error.message
                      : "Failed to upload profile picture.")}
                  </p>
                )}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <SettingsField label="First name">
                <SettingsInput value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </SettingsField>
              <SettingsField label="Last name">
                <SettingsInput value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </SettingsField>
              <SettingsField label="Title">
                <SettingsInput
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Founder and CEO"
                />
              </SettingsField>
              <SettingsField label="Login email">
                <div className="flex gap-2">
                  <SettingsInput value={form.email ?? ""} disabled />
                  <button
                    type="button"
                    className="rounded-md border border-border px-3 text-xs text-muted-foreground"
                    disabled
                  >
                    Edit
                  </button>
                </div>
              </SettingsField>
              <SettingsField label="Password">
                <div className="flex gap-2">
                  <SettingsInput type="password" value="**********" disabled />
                  <button
                    type="button"
                    className="rounded-md border border-border px-3 text-xs text-muted-foreground"
                    disabled
                  >
                    Edit
                  </button>
                </div>
              </SettingsField>
              <SettingsField label="Location">
                <SettingsInput
                  value={form.location ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                />
              </SettingsField>
            </div>
          </SettingsSection>

          <SettingsSection
            title="Appearance"
            description="This setting applies after sign in. Public pages always follow your device."
          >
            <SettingsField label="Theme">
              <select
                value={form.themePreference ?? "system"}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    themePreference: event.target.value as "system" | "light" | "dark",
                  }))
                }
                className="w-full max-w-sm rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <option value="system">Use device setting</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </SettingsField>
          </SettingsSection>

          <SettingsSection
            title="CRM connection"
            description="Link your CRM user for deal sync from Integrations."
            action={
              <Link to="/settings/integrations" className="text-xs text-brand hover:underline">
                Manage integrations
              </Link>
            }
          >
            {profile?.hubspotPortalId ? (
              <div className="space-y-2 text-sm">
                <p>
                  <span className="text-muted-foreground">Connected as:</span>{" "}
                  {profile.email}
                </p>
                <p>
                  <span className="text-muted-foreground">Team Portal ID:</span>{" "}
                  {profile.hubspotPortalId}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No CRM user linked yet. Connect HubSpot or Salesforce under Integrations.
              </p>
            )}
          </SettingsSection>

          <SettingsSection title="Restrictions">
            <SettingsField
              label="Credit Limit"
              hint="Leave this field blank if no limit is required"
            >
              <SettingsInput
                type="number"
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
                placeholder="No limit"
              />
            </SettingsField>
          </SettingsSection>

          <SettingsSection title="LinkedIn discovery">
            <p className="mb-3 text-sm text-muted-foreground">
              How would you like to find people on LinkedIn?
            </p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="li"
                  checked={linkedinMode === "normal"}
                  onChange={() => setLinkedinMode("normal")}
                />
                Use normal LinkedIn search
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="li"
                  checked={linkedinMode === "salesnav"}
                  onChange={() => setLinkedinMode("salesnav")}
                />
                Use LinkedIn Sales Navigator (requires Sales Navigator subscription)
              </label>
            </div>
          </SettingsSection>

          <SettingsSection title="Targeting preferences">
            <div className="grid gap-4 sm:grid-cols-2">
              <SettingsField label="Currency">
                <SettingsInput
                  value={form.currency ?? "USD"}
                  onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                />
              </SettingsField>
              <SettingsField label="Min. deal size">
                <SettingsInput
                  type="number"
                  value={form.targetDealSizeMin ?? ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, targetDealSizeMin: Number(e.target.value) }))
                  }
                />
              </SettingsField>
              <SettingsField label="Max. deal size">
                <SettingsInput
                  type="number"
                  value={form.targetDealSizeMax ?? ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, targetDealSizeMax: Number(e.target.value) }))
                  }
                />
              </SettingsField>
            </div>
            <SettingsField label="Target segments">
              <SettingsInput
                value={(form.targetSegments ?? []).join(", ")}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    targetSegments: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  }))
                }
              />
            </SettingsField>
            <SettingsField label="Assistant memory">
              <SettingsTextarea
                rows={3}
                value={form.assistantMemory ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, assistantMemory: e.target.value }))}
              />
            </SettingsField>
          </SettingsSection>
        </>
      )}

      {tab === "Multi-factor authentication" && (
        <SettingsSection
          title="Multi-factor authentication"
          description="Add an extra layer of security to your login."
        >
          <SettingsToggleRow
            label="Email one-time codes"
            description="Receive a verification code by email when signing in from a new device."
            checked={mfaEmail}
            onChange={setMfaEmail}
          />
          <SettingsToggleRow
            label="Authenticator app"
            description="Use an authenticator app (TOTP) for MFA. Wire-up coming soon."
            checked={mfaApp}
            onChange={setMfaApp}
          />
        </SettingsSection>
      )}

      {tab === "Custom fields" && (
        <SettingsSection
          title="Custom fields"
          description="Personal custom fields for people and companies you own."
        >
          <p className="text-sm text-muted-foreground">
            Workspace-wide fields live under Data management → Objects, fields, stages.
          </p>
          <Link
            to="/settings/opportunities"
            className="inline-block text-sm text-brand hover:underline"
          >
            Open Deal fields & stages
          </Link>
        </SettingsSection>
      )}

      {tab === "Email settings" && (
        <SettingsSection title="Email settings">
          <SettingsToggleRow
            label="Track email opens"
            description="Record opens when available via your connected mailbox."
            checked
            onChange={() => undefined}
            disabled
          />
          <SettingsToggleRow
            label="Track link clicks"
            description="Record clicks in outbound emails when tracking is enabled."
            checked
            onChange={() => undefined}
            disabled
          />
          <Link to="/settings/account/mailboxes" className="text-sm text-brand hover:underline">
            Manage mailboxes & domains
          </Link>
        </SettingsSection>
      )}

      {tab === "Conversations" && (
        <SettingsSection
          title="Conversations preferences"
          description="Personal recording visibility. Team defaults are managed by admins."
        >
          <SettingsToggleRow
            label="Show conversation recordings on deals"
            description="Surface synced meeting recordings in Conversations and deal timelines."
            checked
            onChange={() => undefined}
            disabled
          />
          <Link to="/settings/conversations" className="text-sm text-brand hover:underline">
            Open Conversation recording settings
          </Link>
        </SettingsSection>
      )}
    </div>
  );
}
